import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ScanLine,
  ArrowLeft,
  Ticket,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

export default function GuiaRedeemPage() {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { ok, coupon } | { error }

  const handleRedeem = async (e) => {
    e?.preventDefault?.();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post("/guia/coupons/redeem", { code: trimmed });
      setResult({ ok: true, coupon: data });
      toast.success("Cupón canjeado correctamente");
      setCode("");
    } catch (err) {
      const msg = err.response?.data?.detail || "No se pudo canjear el cupón";
      setResult({ error: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand pb-12" data-testid="guia-redeem-page">
      <div className="container-main py-6 max-w-2xl">
        <Link
          to="/guia"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-agave mb-4"
          data-testid="redeem-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la Guía
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
            <ScanLine className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-agave">Canjear cupón</h1>
            <p className="text-sm text-neutral-600">
              Ingresa el código que te muestra el cliente para validar su descuento
            </p>
          </div>
        </div>

        <form onSubmit={handleRedeem} className="card-base mb-6" data-testid="redeem-form">
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Código del cupón
          </label>
          <Input
            type="text"
            placeholder="Ej: 7K9XAB"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="text-lg font-mono tracking-widest text-center uppercase"
            maxLength={8}
            data-testid="redeem-code-input"
            autoFocus
          />
          <p className="text-xs text-neutral-500 mt-2">
            El cliente verá un código de 6 letras y números en la app o un QR escanéable.
          </p>
          <Button
            type="submit"
            disabled={!code.trim() || submitting}
            className="w-full btn-primary mt-4"
            data-testid="redeem-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Validando…
              </>
            ) : (
              "Validar y canjear"
            )}
          </Button>
        </form>

        {/* Result */}
        {result?.ok && result.coupon && (
          <div
            className="card-base border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"
            data-testid="redeem-success"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-agave">¡Canjeado!</h2>
                <Badge className="bg-emerald-600 text-white border-0 mt-1">
                  {result.coupon.business_name}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm mt-4">
              <div>
                <p className="text-xs uppercase text-neutral-500">Descuento</p>
                <p className="font-bold text-terracotta text-lg">
                  {result.coupon.discount_percent
                    ? `-${Math.round(result.coupon.discount_percent)}%`
                    : "Descuento"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-neutral-500">Código</p>
                <p className="font-mono font-bold tracking-widest">{result.coupon.code}</p>
              </div>
            </div>
            {result.coupon.discount_details && (
              <p className="text-sm text-neutral-700 mt-3 p-3 bg-white/80 rounded-lg">
                {result.coupon.discount_details}
              </p>
            )}
          </div>
        )}

        {result?.error && (
          <div
            className="card-base border-red-200 bg-red-50 flex items-start gap-3"
            data-testid="redeem-error"
          >
            <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">No se pudo canjear</p>
              <p className="text-sm text-red-700">{result.error}</p>
            </div>
          </div>
        )}

        <div className="mt-8 card-base bg-amber-50 border-amber-200 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-medium mb-1">Solo el dueño del negocio puede canjear</p>
            <p>
              Tu sesión debe estar autenticada como el propietario del listado al que pertenece el cupón.
              Cada cupón solo puede canjearse una vez.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
